/**
 * What a FIELD KIND means to a value — headless, so the import mapper and a
 * bulk editor can use it with no React host (#1679 slice 2).
 *
 * `core.field_meta.kind` is the platform's one field vocabulary, fixed by a
 * CHECK constraint (`packages/core/migrations/0031_addresses.sql:171-173`).
 * Every surface that edits a governed record has to answer the same two
 * questions about it — *what does a browser control hand back for this kind*
 * and *what does blank mean here* — and, measured on `origin/main` before this
 * was written, they answer differently:
 *
 *   `packages/ui/src/controls/Form.tsx:127`            blank number -> null
 *   `packages/ui/src/grid/ColumnFilterEditor.tsx:197`  blank number -> ''
 *   `apps/console/src/bidDesk/BidDesk.tsx:489`         blank number -> undefined
 *   `packages/ui/src/grid/DataGrid.tsx:452`            blank number -> 0, and a
 *                                                      typed `-5` commits as 5
 *
 * Four answers for the same column depending on which surface you edited it
 * from, and the fourth silently discards a minus sign. `EntityForm` resolves it
 * a fifth way, and its own comment already names this module as the place the
 * answer belongs:
 *
 *   "One set keeps the form, grid and future typed-editor registry from
 *    growing per-entity fixes."  — EntityForm.tsx:118-122
 *
 * ── SCOPE: parse, plus the ONE display convention that has been ruled ───────
 * The display side had a genuine semantic disagreement that was nobody's to
 * settle in a refactor. It has now been RULED (Codex, mailbox #1706 comment
 * 52108): a percent is a WHOLE NUMBER. `20` means 20%. `formatPercent` below
 * is that ruling, and it is the only formatter here — money and date were not
 * ruled and their surfaces still disagree, so unifying them would change
 * shipped output on a question nobody has answered.
 */

/**
 * The kinds the CHECK constraint allows, in its order.
 *
 * ── `multiselect` (#1928) ───────────────────────────────────────────────────
 * This list used to end at `phone`, and said so in prose: "`multiselect` is NOT
 * among them and is not invented here — but the console does ship it". That was
 * an accurate report of a contradiction, not a design. The control existed
 * (`packages/ui/src/controls/MultiSelect.tsx`), one host had an arm for it
 * (`crm/RecordPage.tsx`), the fixture registered a column as one — and the
 * database REFUSED the registration, because `multiselect` was absent from
 * `field_meta_kind_check`. So the kind was reachable only from a fixture, and
 * `#1923` correctly deleted that fixture row as something no deployment could
 * have.
 *
 * `packages/core/migrations/2026090812_a_set_of_choices_is_one_field.sql`
 * settles it the other way: `multiselect` is a DISTINCT kind (ruling, mailbox
 * #1958 comment 61687 — not scalar `option` with a cardinality flag), the CHECK
 * admits it, and the three governed-vocabulary registrars in `2026082918`
 * accept it alongside `option` so it can be given a catalogue through a guarded
 * door.
 *
 * The order below is the CONSTRAINT's order, and that migration appends rather
 * than rewrites — it reads the live list out of `pg_get_constraintdef` and
 * asserts the result is the old list plus one entry — so this stays in step by
 * appending too.
 */
export const FIELD_KINDS = [
  'text', 'number', 'money', 'percent', 'date', 'datetime', 'boolean',
  'ref', 'option', 'uuid', 'json', 'country', 'email', 'phone', 'multiselect',
] as const

export type FieldKind = (typeof FIELD_KINDS)[number]

export const isFieldKind = (kind: string): kind is FieldKind =>
  (FIELD_KINDS as readonly string[]).includes(kind)

/**
 * The kinds where a browser's empty string is NOT the value.
 *
 * Carried verbatim from `EntityForm.tsx:123-125`, including the reasoning:
 * "Browser controls express 'nothing selected' as an empty string. That is a
 * valid value for text, but PostgreSQL date, numeric, option and reference
 * columns use NULL for absence."
 *
 * `datetime` is deliberately NOT in this list even though it is plainly a typed
 * column, because it is not in EntityForm's set either and this module carries
 * current behaviour rather than improving it in passing. Widening it is a
 * behaviour change on every datetime column in the fleet and wants its own
 * slice; the omission is recorded here rather than silently corrected.
 *
 * `multiselect` is NOT in this list either, and for the opposite reason — not
 * caution, but a different answer. Its blank is an EMPTY ARRAY, not NULL: the
 * one column that carries it is `not null default '{}'` and the matcher that
 * reads it treats the empty set as "every source"
 * (`modules/tenders/migrations/0009_discovery.sql:558`). Its control also never
 * hands back an empty string — `MultiSelect`'s `onChange` is
 * `(next: string[]) => void` — so the browser-blank question this set answers
 * does not arise for it at all.
 */
export const TYPED_BLANK_KINDS: ReadonlySet<string> = new Set([
  'date', 'ref', 'option', 'number', 'money', 'percent',
])

export const isTypedBlank = (kind: string): boolean => TYPED_BLANK_KINDS.has(kind)

/** The kinds whose stored value is a number rather than a string. */
export const NUMERIC_KINDS: ReadonlySet<string> = new Set(['number', 'money', 'percent'])

export const isNumericKind = (kind: string): boolean => NUMERIC_KINDS.has(kind)

/**
 * What a control's raw output means for this kind.
 *
 * Returns the value to store. The three cases that surfaces disagreed about:
 *
 *  · BLANK on a typed kind is `null`, not `''`, `0` or `undefined`. Blank and
 *    zero are different business facts — `EntityForm.tsx:152-154` is the only
 *    place that currently says so, and `DataGrid.tsx:452` is the place that
 *    currently loses it.
 *
 *  · A numeric string that is NOT a number is returned AS TYPED, never coerced
 *    to `NaN` or to `0`. A parser that silently produces `0` from "abc" has
 *    destroyed the keystrokes and reported nothing; returning the text lets the
 *    caller's validation say what is wrong. This is the behaviour #1680's
 *    settings form already adopted.
 *
 *  · Whitespace counts as blank on a typed kind. `OutcomePanel.tsx:163` trims
 *    and the other four parsers do not, so " " stored `null` on one surface and
 *    `NaN` on the rest.
 */
export function parseFieldValue(kind: string, raw: unknown): unknown {
  if (typeof raw !== 'string') return raw

  if (isTypedBlank(kind) && raw.trim() === '') return null

  if (isNumericKind(kind)) {
    const n = Number(raw)
    return Number.isFinite(n) ? n : raw
  }

  return raw
}

/**
 * What "empty" is for this kind, for seeding a control.
 *
 * A control needs a string; the STORE needs `null` for a typed kind. Those are
 * different questions and this answers the second — `emptyControlValue` below
 * answers the first.
 */
export const emptyFieldValue = (kind: string): null | '' => (isTypedBlank(kind) ? null : '')

/**
 * What a text control should show for a value that is absent.
 *
 * Always `''`: React logs "a component is changing an uncontrolled input to be
 * controlled" the moment a control's value goes `undefined` -> string, and a
 * `null` in a text input renders the literal string in some browsers. Stated as
 * a function rather than inlined because the asymmetry with `emptyFieldValue`
 * is the whole point and has been got wrong on at least four surfaces.
 */
export const emptyControlValue = (): string => ''

/**
 * A percent, rendered — the ruled convention (Codex, #1706 comment 52108).
 *
 *   "Percent convention — WHOLE NUMBER. 20 means 20%; 0.2 means 0.2%. …
 *    The portal's `Intl style:'percent'` fraction rendering is the defect.
 *    Put the convention in the headless formatter registry so every host
 *    consumes one answer."
 *
 * ── The evidence, because a convention with no evidence is a preference ─────
 * Measured across the fleet before this was written. Five of the six columns
 * registered `kind='percent'` store whole numbers, every arithmetic site
 * DIVIDES by 100 to use one, and the convention is already test-pinned in the
 * places it legally matters — `<cbc:Percent>21.00</cbc:Percent>` in the EN16931
 * UBL (`modules/billing/test/billing.test.ts:333`) and `getRate('BE',
 * 'standard') === 21` (`packages/vat/test/sandbox-parity.test.ts:66`).
 *
 * The live defect this closes: `apps/portal/src/viewColumns.tsx:174` used
 * `Intl` with `style: 'percent'`, which multiplies by 100. Exactly two columns
 * reach it — `billing_invoice_line.discount_pct` and `cpq_quote_line.discount_pct`
 * — so a customer reading their own quote or invoice saw a negotiated **5%
 * discount rendered as 500%**.
 *
 * ── Trailing zeros are DECIDED here, because the ruling is silent on them ────
 * PostgREST returns `numeric` as a string, and these columns are `numeric(6,3)`
 * and `numeric(5,2)`, so the wire value is `"5.000"` / `"25.00"`. Removing only
 * the ×100 would put `5.000%` on that quote line — correct in scale and worse
 * to read than what it replaced. The repo already has an answer and it is used
 * on the legal invoice: `trim_scale()`
 * (`modules/documents-billing/migrations/0002_legal_invoice.sql:76,85`). So
 * trailing zeros go, and `5.5` keeps its half.
 *
 * ── Deliberately NOT done ───────────────────────────────────────────────────
 * · No locale grouping and no `Intl`. `packages/experience/src/render.ts` is
 *   L0 HTML and its header calls the output self-contained; a formatter that
 *   varied by host locale could not be used there. A percent is bounded at 100
 *   by CHECK or by `rules` on every registered column but one, so there is
 *   nothing to group.
 * · The unit is NOT appended. `partners_agreement.margin_percent` and
 *   `partners_commission.rate_percent` both carry `unit = '%'` in their
 *   registration, and a formatter that appended it would render `20%%`.
 * · `investigations_link.confidence` is `kind='percent'` with a CHECK of
 *   `between 0 and 1` — the one column in the fleet the ruling contradicts. It
 *   has no registered view and never reaches a metadata-driven renderer; its
 *   only display is hand-coded in `ContextWorkspace.tsx:276` and is correct.
 *   Routing that component through here would turn `86% confidence` into
 *   `0.86%`. It is left alone, and reported rather than silently re-kinded.
 */
export function formatPercent(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  // `trim_scale` semantics: 5.000 -> "5", 25.00 -> "25", 5.50 -> "5.5".
  // `parseFloat(n.toFixed(...))` would re-introduce float noise; String(n)
  // already drops trailing zeros because n is a Number by here.
  return `${String(n)}%`
}

/**
 * The display side of the field-kind registry — currently percent only.
 *
 * Returns `null` for a kind this registry does not yet own, so a caller can
 * tell "no opinion" from "the answer is an empty string". A host that gets
 * `null` keeps whatever it does today; that is what stops this from silently
 * changing money and date output on a question that has not been ruled.
 */
export function formatFieldValue(kind: string, value: unknown): string | null {
  return kind === 'percent' ? formatPercent(value) : null
}
